import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Price, ChargeNote } from "@/lib/currency";
import { waLink } from "@/data/content";
import { Nav } from "@/components/Nav";
import { Seo } from "@/components/Seo";
import { Footer } from "@/components/Footer";
import { FadeUp } from "@/components/Reveal";
import { EnquiryModal } from "@/components/EnquiryModal";

const CATEGORY_LABEL = {
  guide: "Digital guide — instant PDF download",
  service: "Planning service",
  vip: "VIP arrival package",
  merch: "JÀWÀ shop — small-batch order",
};

const inputCls =
  "w-full border border-jawa-espresso/20 bg-white px-4 py-3 text-sm text-jawa-espresso outline-none focus:border-jawa-turquoise";

const AIRPORT_NAMES = { LOS: "Lagos (MMIA)", ABV: "Abuja (Nnamdi Azikiwe)" };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const mdInWindow = (md, start, end) => (start <= end ? start <= md && md <= end : md >= start || md <= end);
const parseMd = (s) => s.split("-").map(Number);

const VipBookingForm = ({ product, onBusy }) => {
  const vcfg = product.vip_config || {};
  const pickupIncluded = !!vcfg.pickup_included;
  const airports = vcfg.airports || ["LOS", "ABV"];
  const addonUsd = vcfg.pickup_addon_usd || 0;
  const [form, setForm] = useState({
    name: "", email: "", whatsapp: "", arrival_date: "", airport: airports[0] || "",
    flight_number: "", arrival_time: "", passengers: 1, luggage: "", notes: "",
    add_pickup: false, delivery_notes: "",
  });
  const [season, setSeason] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (vcfg.season_key) {
      api.get("/site/vip-season").then((r) => setSeason(r.data)).catch(() => {});
    }
  }, [vcfg.season_key]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const needsAirport = pickupIncluded || form.add_pickup;
  const passengers = Math.max(1, parseInt(form.passengers, 10) || 1);
  const total = (product.price || 0) * passengers + (!pickupIncluded && form.add_pickup ? addonUsd : 0);

  const dateError = useMemo(() => {
    if (!form.arrival_date) return "";
    const d = new Date(`${form.arrival_date}T12:00:00`);
    const m = d.getMonth() + 1;
    if ((vcfg.blocked_months || []).includes(m)) {
      return `${product.title} is not available for ${MONTHS[m - 1]} arrivals — the Detty December VIP Box is the festive-season option.`;
    }
    if (vcfg.season_key && season) {
      const ok = mdInWindow([m, d.getDate()], parseMd(season.start), parseMd(season.end));
      if (!ok) return `This is a seasonal package — available for arrivals between ${season.start_label} and ${season.end_label} only.`;
    }
    return "";
  }, [form.arrival_date, vcfg, season, product.title]);

  const submit = async (e) => {
    e.preventDefault();
    if (dateError) { setError(dateError); return; }
    setError("");
    setBusy(true);
    onBusy(true);
    try {
      const res = await api.post("/paystack/initialize", {
        kind: "product",
        product_id: product._id || product.id,
        email: form.email,
        name: form.name,
        booking: {
          whatsapp: form.whatsapp,
          arrival_date: form.arrival_date,
          airport: needsAirport ? form.airport : "",
          flight_number: form.flight_number,
          arrival_time: form.arrival_time,
          passengers,
          luggage: form.luggage,
          notes: form.notes,
          add_pickup: !pickupIncluded && form.add_pickup,
          delivery_notes: form.delivery_notes,
        },
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
      onBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="mt-8 max-w-md space-y-3 border border-jawa-espresso/15 bg-white p-6" data-testid="vip-booking-form">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso/60">Booking details</p>
      <input required value={form.name} onChange={set("name")} placeholder="Full name" data-testid="vip-book-name" className={inputCls} />
      <input required type="email" value={form.email} onChange={set("email")} placeholder="Email address" data-testid="vip-book-email" className={inputCls} />
      <input required value={form.whatsapp} onChange={set("whatsapp")} placeholder="WhatsApp number (with country code)" data-testid="vip-book-whatsapp" className={inputCls} />
      <div>
        <label className="mb-1 block text-[10px] uppercase tracking-[0.15em] text-jawa-espresso/50">Arrival / start date</label>
        <input required type="date" value={form.arrival_date} onChange={set("arrival_date")} data-testid="vip-book-date" className={inputCls} />
        {vcfg.season_key && season && (
          <p className="mt-1 text-[11px] text-jawa-espresso/55" data-testid="vip-book-season-note">
            Seasonal package — bookable for arrivals between {season.start_label} and {season.end_label}.
          </p>
        )}
        {(vcfg.blocked_months || []).length > 0 && (
          <p className="mt-1 text-[11px] text-jawa-espresso/55" data-testid="vip-book-blocked-note">
            Not available for December or January arrivals.
          </p>
        )}
        {dateError && <p className="mt-1 text-[11px] font-semibold text-jawa-coral" data-testid="vip-book-date-error">{dateError}</p>}
      </div>

      {!pickupIncluded && (
        <label className="flex items-start gap-3 border border-jawa-stone bg-jawa-ivory p-3 text-sm text-jawa-espresso" data-testid="vip-book-pickup-addon">
          <input type="checkbox" checked={form.add_pickup} onChange={set("add_pickup")} className="mt-1" data-testid="vip-book-pickup-checkbox" />
          <span>
            Add chauffeur-driven airport pickup (Lagos or Abuja) — <Price amount={addonUsd} from="USD" /> per arrival, subject to availability.
          </span>
        </label>
      )}

      {needsAirport ? (
        <>
          <select value={form.airport} onChange={set("airport")} data-testid="vip-book-airport" className={inputCls} aria-label="Arrival airport">
            {airports.map((a) => (
              <option key={a} value={a}>{AIRPORT_NAMES[a] || a}</option>
            ))}
          </select>
          <div className="grid grid-cols-2 gap-3">
            <input value={form.flight_number} onChange={set("flight_number")} placeholder="Flight number" data-testid="vip-book-flight" className={inputCls} />
            <input type="time" value={form.arrival_time} onChange={set("arrival_time")} data-testid="vip-book-time" className={inputCls} aria-label="Arrival time" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input required type="number" min="1" max="20" value={form.passengers} onChange={set("passengers")} placeholder="Passengers" data-testid="vip-book-passengers" className={inputCls} />
            <input value={form.luggage} onChange={set("luggage")} placeholder="Luggage (e.g. 2 cases)" data-testid="vip-book-luggage" className={inputCls} />
          </div>
        </>
      ) : (
        <>
          <input type="number" min="1" max="20" value={form.passengers} onChange={set("passengers")} placeholder="Number of boxes (per person)" data-testid="vip-book-passengers" className={inputCls} />
          <textarea required rows={2} value={form.delivery_notes} onChange={set("delivery_notes")} placeholder="Destination and delivery / handover arrangements" data-testid="vip-book-delivery" className={inputCls} />
        </>
      )}
      <textarea rows={2} value={form.notes} onChange={set("notes")} placeholder="Anything else we should know (accessibility, pickup requirements…)" data-testid="vip-book-notes" className={inputCls} />

      <div className="border-t border-jawa-stone pt-3 text-sm text-jawa-espresso/70" data-testid="vip-book-summary">
        <div className="flex justify-between"><span>{product.title} × {passengers}</span><Price amount={(product.price || 0) * passengers} from="USD" /></div>
        {!pickupIncluded && form.add_pickup && (
          <div className="mt-1 flex justify-between"><span>Airport pickup add-on</span><Price amount={addonUsd} from="USD" /></div>
        )}
        <div className="mt-2 flex justify-between font-serif text-xl font-medium text-jawa-espresso">
          <span>Total</span><span data-testid="vip-book-total"><Price amount={total} from="USD" /></span>
        </div>
        <p className="mt-2 text-[11px] leading-relaxed text-jawa-espresso/55"><ChargeNote amount={total} from="USD" /></p>
      </div>

      {error && <p className="text-xs font-semibold text-jawa-coral" data-testid="vip-book-error">{error}</p>}
      <button type="submit" disabled={busy || !!dateError} data-testid="vip-book-submit"
        className="flex w-full items-center justify-center gap-2 bg-jawa-espresso px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-coral disabled:opacity-60">
        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
        Continue to payment
      </button>
      <p className="text-[11px] leading-relaxed text-jawa-espresso/50">
        Payment is verified by Paystack before anything is confirmed. Cancellation and refund terms are on our{" "}
        <Link to="/legal" className="underline">legal page</Link>; arrival changes are handled by the concierge team.
      </p>
    </form>
  );
};

export default function ProductPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [missing, setMissing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [enquiring, setEnquiring] = useState(false);

  useEffect(() => {
    api
      .get("/products")
      .then((r) => {
        const found = r.data.find((p) => p._id === productId || p.id === productId);
        if (found) setProduct(found);
        else setMissing(true);
      })
      .catch(() => setMissing(true));
  }, [productId]);

  const buy = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/paystack/initialize", {
        kind: "product",
        product_id: product._id || product.id,
        email,
        name,
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(formatApiError(err));
      setBusy(false);
    }
  };

  const isVip = product?.category === "vip";

  return (
    <div className="bg-jawa-ivory" data-testid="product-page">
      <Nav />
      {product && <Seo title={`${product.title} | JÀWÀ Africa`} description={product.description?.slice(0, 150)} />}
      <main className="mx-auto max-w-6xl px-6 pb-24 pt-32 lg:px-10 lg:pt-40">
        <FadeUp>
          <Link to="/shop" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-espresso/50 transition-colors hover:text-jawa-coral" data-testid="product-back">
            <ArrowLeft className="h-4 w-4" /> Back to shop
          </Link>
        </FadeUp>

        {missing && (
          <FadeUp className="mt-16 text-center" >
            <p className="font-serif text-3xl text-jawa-espresso" data-testid="product-missing">This product isn't available.</p>
            <Link to="/shop" className="mt-6 inline-block bg-jawa-espresso px-8 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-jawa-ivory">Browse the shop</Link>
          </FadeUp>
        )}

        {!product && !missing && (
          <div className="mt-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-jawa-turquoise" /></div>
        )}

        {product && (
          <div className="mt-8 grid gap-10 lg:grid-cols-2">
            <FadeUp>
              {product.image_url ? (
                <div className="overflow-hidden border border-jawa-stone">
                  <img src={product.image_url} alt={product.title} className="h-full max-h-[520px] w-full object-cover" data-testid="product-image" />
                </div>
              ) : (
                <div className="flex h-full min-h-[320px] items-center justify-center border border-jawa-gold/40 bg-jawa-espresso p-10" data-testid="product-image-placeholder">
                  <p className="text-center font-serif text-3xl font-medium leading-snug text-jawa-ivory">{product.title}</p>
                </div>
              )}
            </FadeUp>

            <FadeUp delay={0.1}>
              <p className="text-xs uppercase tracking-[0.3em] text-jawa-coral" data-testid="product-category">{CATEGORY_LABEL[product.category] || "JÀWÀ product"}</p>
              <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight text-jawa-espresso sm:text-5xl" data-testid="product-title">
                {product.title}
              </h1>
              {product.price != null && (
                <p className="mt-5 font-serif text-3xl font-medium text-jawa-espresso" data-testid="product-price">
                  <Price amount={product.price} from={product.currency || "GBP"} />
                  {isVip && <span className="ml-2 text-sm font-sans uppercase tracking-[0.15em] text-jawa-espresso/50">per person</span>}
                </p>
              )}
              <p className="mt-5 whitespace-pre-line text-base leading-relaxed text-jawa-espresso/70" data-testid="product-description">{product.description}</p>

              {isVip && product.checkout === "paystack" ? (
                <VipBookingForm product={product} onBusy={setBusy} />
              ) : product.checkout === "paystack" && product.price != null ? (
                <form onSubmit={buy} className="mt-8 max-w-md space-y-3 border border-jawa-espresso/15 bg-white p-6" data-testid="product-buy-form">
                  <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" data-testid="product-buy-name"
                    className={inputCls} />
                  <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" data-testid="product-buy-email"
                    className={inputCls} />
                  <p className="text-[11px] leading-relaxed text-jawa-espresso/55" data-testid="product-charge-note">
                    <ChargeNote amount={product.price} from={product.currency || "GBP"} />
                    {product.category === "guide" && " Your PDF downloads instantly after payment; if the guide is being finalised it is emailed to you instead — either way your purchase is confirmed immediately."}
                  </p>
                  <button type="submit" disabled={busy} data-testid="product-buy-button"
                    className="flex w-full items-center justify-center gap-2 bg-jawa-espresso px-6 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-ivory transition-colors hover:bg-jawa-coral disabled:opacity-60">
                    {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                    Buy now — <Price amount={product.price} from={product.currency || "GBP"} />
                  </button>
                </form>
              ) : (
                <div className="mt-8 flex flex-wrap gap-4">
                  <button
                    onClick={() => setEnquiring(true)}
                    data-testid="product-enquire-button"
                    className="bg-jawa-coral px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors hover:bg-jawa-espresso"
                  >
                    Enquire & get a quote
                  </button>
                  <a
                    href={waLink(`Hello JÀWÀ — I'm interested in: ${product.title}`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="product-whatsapp"
                    className="inline-flex items-center gap-2 border border-jawa-turquoise px-7 py-3.5 text-xs font-semibold uppercase tracking-[0.25em] text-jawa-turquoise transition-colors hover:bg-jawa-turquoise/10"
                  >
                    WhatsApp <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              )}

              {isVip && (
                <p className="mt-6 max-w-md text-xs leading-relaxed text-jawa-espresso/55" data-testid="product-vip-note">
                  Gift contents marked as curated are confirmed with you before fulfilment — availability and final
                  contents first. Support hours for the seven-day WhatsApp concierge are 08:00–20:00 WAT.
                </p>
              )}
            </FadeUp>
          </div>
        )}
      </main>
      <Footer />
      {enquiring && product && (
        <EnquiryModal
          journey={isVip ? "VIP Arrival Experience" : `Shop — ${product.title}`}
          title={product.title}
          arrival={isVip}
          onClose={() => setEnquiring(false)}
          testid="product-enquiry-modal"
        />
      )}
    </div>
  );
}
